import { useState, useEffect, useCallback } from 'react';
import { getDraws, runDraw } from '../api';
import { colours as c, font, gbp, num, shortDate, monthYear } from '../theme';

const Skeleton = ({ width = '100%', height = '20px', style = {} }) => (
  <div className="skeleton" style={{ width, height, ...style }} />
);

const WinnerCard = ({ place, prize, person, highlight }) => (
  <div style={S.winner}>
    <span style={{ ...S.winnerPlace, color: highlight ? c.sage : c.muted }}>
      {place} · {gbp(prize)}
    </span>
    <span style={S.winnerName}>{person?.name || 'Unknown'}</span>
    <span style={S.winnerEmail}>{person?.email || ''}</span>
  </div>
);

export default function AdminDraws() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDraws(page);
      setData(res.data);
    } catch (err) {
      // interceptor handles auth
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const doRun = async () => {
    setRunning(true);
    setMessage('');
    try {
      await runDraw();
      setMessage('Draw complete. Winners and the treasurer have been emailed.');
      setConfirming(false);
      await load();
    } catch (err) {
      setMessage(err.response?.data?.error || 'The draw could not be run.');
    } finally {
      setRunning(false);
    }
  };

  if (loading && !data) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <Skeleton height="180px" />
        <Skeleton height="240px" />
      </div>
    );
  }

  const pending = data?.pending;
  const summary = data?.summary;
  const draws = data?.draws || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

      {summary && (
        <span style={S.summaryLine}>
          {summary.drawCount === 0
            ? 'No draws yet · runs itself at 9am on the 1st'
            : `${num(summary.drawCount)} draw${summary.drawCount === 1 ? '' : 's'}${summary.since ? ` since ${summary.since}` : ''} · ${gbp(summary.totalPaidOut)} paid out · runs itself at 9am on the 1st`}
        </span>
      )}

      {message && (
        <div style={S.message} onClick={() => setMessage('')}>
          {message} <span style={{ color: c.muted }}>(click to dismiss)</span>
        </div>
      )}

      {/* ready to draw */}
      {pending && (
        <div style={S.readyGrid} className="grid-2">
          <div style={S.readyCard}>
            <span style={S.eyebrowOnTeal}>
              {pending.alreadyDrawn ? 'Already drawn' : 'Ready to draw'}
            </span>
            <span style={S.readyMonth}>{pending.month}</span>
            <span style={S.readyText}>
              {pending.alreadyDrawn
                ? `${pending.month} has already been drawn. The next draw runs on the 1st of next month.`
                : pending.entrants < 3
                  ? `Only ${num(pending.entrants)} member${pending.entrants === 1 ? ' is' : 's are'} covered for ${pending.month}. At least three are needed to draw.`
                  : `${num(pending.entrants)} members are in the hat. Three winners will share ${gbp(pending.first + pending.second + pending.third)} of the ${gbp(pending.pot)} pot; ${gbp(pending.retained)} stays with the association.`}
            </span>
            {pending.canRun && !confirming && (
              <button style={S.readyBtn} onClick={() => setConfirming(true)}>
                Run the {pending.month.split(' ')[0]} draw
              </button>
            )}
          </div>

          {confirming && pending.canRun ? (
            <div style={S.confirmCard}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={S.warnDot} />
                <span style={S.confirmTitle}>Draw {pending.month} now?</span>
              </div>
              <p style={S.confirmText}>
                This picks three winners at random from the {num(pending.entrants)} covered
                members, records the amounts and emails all three. It can't be undone — if
                something looks wrong, check the members list first.
              </p>
              <div style={S.breakdown}>
                <div style={S.bdRow}>
                  <span style={S.bdLabel}>Pot</span>
                  <span style={S.bdValue}>{gbp(pending.pot)}</span>
                </div>
                <div style={S.bdRow}>
                  <span style={S.bdLabel}>
                    Prizes ({pending.percents.first} / {pending.percents.second} / {pending.percents.third}%)
                  </span>
                  <span style={S.bdValue}>
                    {gbp(pending.first)} · {gbp(pending.second)} · {gbp(pending.third)}
                  </span>
                </div>
                <div style={S.bdRow}>
                  <span style={S.bdLabel}>Retained for the fields</span>
                  <span style={{ ...S.bdValue, color: c.sage, fontWeight: 600 }}>
                    {gbp(pending.retained)}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 'auto', flexWrap: 'wrap' }}>
                <button style={S.confirmBtn} onClick={doRun} disabled={running}>
                  {running ? 'Running…' : 'Yes, run the draw'}
                </button>
                <button style={S.cancelBtn} onClick={() => setConfirming(false)} disabled={running}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={S.infoCard}>
              <span style={S.eyebrow}>How the draw works</span>
              <p style={S.confirmText}>
                At 9am on the 1st of each month the draw runs automatically for the month
                that has just ended. Everyone whose membership covered any part of that
                month goes into the hat, once each. Three names come out at random and
                are emailed the same morning, along with the treasurer.
              </p>
            </div>
          )}
        </div>
      )}

      {/* past draws */}
      <div style={S.pastCard}>
        <div style={S.pastHead}>
          <span style={{ fontSize: 15, fontWeight: 600, color: c.ink }}>Past draws</span>
          <span style={S.small}>Most recent first</span>
        </div>

        {draws.length === 0 ? (
          <div style={S.empty}>
            <span style={S.emptyTitle}>No draws yet</span>
            <span style={S.small}>The first draw will appear here once it's run.</span>
          </div>
        ) : (
          draws.map(d => (
            <div key={d.id} style={S.drawRow} className="grid-2">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={S.drawMonth}>{monthYear(d.draw_date)}</span>
                <span style={S.small}>Drawn {shortDate(d.drawnOn)}, 9:00am</span>
                <span style={{ ...S.small, fontVariantNumeric: 'tabular-nums' }}>
                  Pot {gbp(d.pot)} · {num(d.entrants)} in
                </span>
              </div>
              <div style={S.winnerGrid}>
                <WinnerCard place="1st" prize={d.first_prize} person={d.first} highlight />
                <WinnerCard place="2nd" prize={d.second_prize} person={d.second} />
                <WinnerCard place="3rd" prize={d.third_prize} person={d.third} />
              </div>
            </div>
          ))
        )}

        {data?.pagination && data.pagination.totalPages > 1 && (
          <div style={S.pagination}>
            <span style={S.small}>
              Page {data.pagination.page} of {data.pagination.totalPages}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={page <= 1 ? S.pageBtnOff : S.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <button
                style={page >= data.pagination.totalPages ? S.pageBtnOff : S.pageBtn}
                disabled={page >= data.pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  summaryLine: { fontSize: 14, color: c.muted },
  small: { fontSize: 13, color: c.muted, lineHeight: 1.5 },
  eyebrow: {
    fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: c.muted,
  },
  eyebrowOnTeal: {
    fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: c.onTealDim,
  },

  message: {
    background: c.activeBg, color: c.activeFg, padding: '13px 16px',
    borderRadius: 3, fontSize: 14, cursor: 'pointer',
  },

  readyGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 22,
  },
  readyCard: {
    background: c.teal, borderRadius: 3, padding: '26px 28px',
    display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0,
  },
  readyMonth: {
    fontFamily: font.serif, fontSize: 'clamp(26px, 3.4vw, 34px)',
    color: c.onTeal, lineHeight: 1.05,
  },
  readyText: { fontSize: 15, color: '#cfdcd6', lineHeight: 1.55 },
  readyBtn: {
    alignSelf: 'flex-start', marginTop: 4, padding: '14px 22px', border: 'none',
    background: c.paper, color: c.ink, borderRadius: 3, fontFamily: font.sans,
    fontSize: 15, fontWeight: 600, cursor: 'pointer', minHeight: 44,
  },

  confirmCard: {
    background: c.card, border: '1px solid #c2b9a4', borderRadius: 3,
    padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0,
  },
  infoCard: {
    background: c.card, border: `1px solid ${c.hairline}`, borderRadius: 3,
    padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0,
  },
  warnDot: { width: 9, height: 9, borderRadius: '50%', background: c.lapsedFg, flex: 'none' },
  confirmTitle: { fontSize: 16, fontWeight: 600, color: c.ink },
  confirmText: { margin: 0, fontSize: 14, lineHeight: 1.55, color: c.body },

  breakdown: {
    display: 'flex', flexDirection: 'column', gap: 9, padding: '15px 17px',
    background: c.cardAlt, borderRadius: 3, fontSize: 14,
    fontVariantNumeric: 'tabular-nums',
  },
  bdRow: { display: 'flex', justifyContent: 'space-between', gap: 12 },
  bdLabel: { color: c.muted },
  bdValue: { color: c.ink, textAlign: 'right' },

  confirmBtn: {
    padding: '12px 20px', border: `1px solid ${c.ink}`, background: c.ink,
    color: c.onTeal, borderRadius: 3, fontFamily: font.sans, fontSize: 14,
    fontWeight: 600, cursor: 'pointer', minHeight: 44,
  },
  cancelBtn: {
    padding: '12px 20px', border: '1px solid #c9c2b1', background: c.card,
    color: c.ink, borderRadius: 3, fontFamily: font.sans, fontSize: 14,
    cursor: 'pointer', minHeight: 44,
  },

  pastCard: {
    background: c.card, border: `1px solid ${c.hairline}`, borderRadius: 3,
    display: 'flex', flexDirection: 'column',
  },
  pastHead: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    padding: '20px 26px 15px', borderBottom: `1px solid ${c.hairline}`,
    gap: 10, flexWrap: 'wrap',
  },
  drawRow: {
    display: 'grid', gridTemplateColumns: '210px minmax(0,1fr)', gap: 26,
    padding: '20px 26px', borderBottom: `1px solid ${c.hairSoft}`,
  },
  drawMonth: { fontFamily: font.serif, fontSize: 23, color: c.ink },
  winnerGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 16,
  },
  winner: {
    background: c.cardAlt, borderRadius: 3, padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0,
  },
  winnerPlace: { fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  winnerName: { fontSize: 15, color: c.ink },
  winnerEmail: {
    fontSize: 12, color: c.muted,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },

  empty: {
    padding: '40px 26px', display: 'flex', flexDirection: 'column',
    gap: 6, alignItems: 'center', textAlign: 'center',
  },
  emptyTitle: { fontFamily: font.serif, fontSize: 19, color: c.ink },

  pagination: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 26px', gap: 12, flexWrap: 'wrap',
  },
  pageBtn: {
    padding: '8px 16px', border: `1px solid ${c.hairline}`, borderRadius: 3,
    background: c.card, cursor: 'pointer', fontSize: 13, color: c.teal,
    fontFamily: font.sans,
  },
  pageBtnOff: {
    padding: '8px 16px', border: `1px solid ${c.hairline}`, borderRadius: 3,
    background: c.cardAlt, cursor: 'not-allowed', fontSize: 13, color: '#a0aec0',
    fontFamily: font.sans,
  },
};
